import React, { useContext, useRef } from 'react';
import { AppContext } from 'src/components/app-context';
import { TranslateFunc } from 'src/components/translate';
import { ButtonType } from '@prisjakt/ui/button';
import { trackPriceListFilterUpdate } from '../tracking';
import { useOfferListFilterContext } from '../offer-list-filter-context';
import { FilterType, IFilter } from 'src/components/price-list/types';
import { resetVariantFilters } from './utils';
import { IProductCondition, IProductConditionsProps } from './types';
import { ButtonWrapper, ConditionHeader, SelectableButton } from './styles';
import { TitleMediumText } from '@prisjakt/ui/text/variants';

export const ProductConditions: React.FC<React.PropsWithChildren<
  IProductConditionsProps
>> = props => {
  const { values, showTitle } = props;
  const {
    getFilter,
    getActiveFilters,
    updateMultipleFilters,
  } = useOfferListFilterContext();
  const { t } = useContext(AppContext);
  const instanceId = useRef(Math.random()); // Since this is used inside media queries, we get aria failiures on non-unique keys without this

  const conditionFilter = getFilter(FilterType.CONDITION);

  const selectedCondition: string = conditionFilter?.selection?.length
    ? conditionFilter?.selection[0]
    : 'new';

  const handleSelectedCondition = (cond: IProductCondition) => {
    if (cond.name !== selectedCondition) {
      trackPriceListFilterUpdate(cond.name, false);
      const updatedConditionFilter = {
        ...conditionFilter,
        active: true,
        selection: [cond.name],
      } as IFilter;
      const updatedVariantFilters = resetVariantFilters(getActiveFilters());
      updateMultipleFilters([...updatedVariantFilters, updatedConditionFilter]);
    }
  };

  if (values.length <= 1) {
    return null;
  }

  return (
    <div>
      {showTitle() && (
        <ConditionHeader>
          <TitleMediumText>
            {t('product', 'price-filter.condition-title')}
          </TitleMediumText>
        </ConditionHeader>
      )}
      <ButtonWrapper>
        {values.map(condition => {
          const key = `${condition.name}-${instanceId.current}`;
          const name = getConditionName(condition.name, t);

          return (
            <SelectableButton
              key={key}
              onClick={() => handleSelectedCondition(condition)}
              selected={condition.name === selectedCondition}
              ariaLabel={name}
              type={ButtonType.OUTLINED}
            >
              {name}
            </SelectableButton>
          );
        })}
      </ButtonWrapper>
    </div>
  );
};

const getConditionName = (type: 'new' | 'used', t: TranslateFunc): string => {
  switch (type) {
    case 'used':
      return t('product', 'price-filter.used-short');
    case 'new':
    default:
      return t('product', 'price-filter.new');
  }
};
